// Índices CPO-ceo (033) — función pura, calculada en memoria desde el estado
// del formulario, NO tipeada a mano. Reglas del instructivo del bloque H/J
// (SNS-MSP/HCU-form.033/2021, pág. 164 del PDF fuente):
//  - Endodoncia por realizar (triángulo rojo) = cariada; endodoncia realizada
//    (triángulo azul) = obturada.
//  - Corona (azul) = obturada.
//  - Prótesis fija realizada: las coronas usadas como pilares cuentan como
//    obturadas; los dientes reemplazados se marcan como perdidos en su pieza.
//  - Prótesis removible = perdidas. Prótesis total = perdidas (los terceros
//    molares no se toman en cuenta; no son piezas índice, así que no aplica aquí).
//  - Pieza restaurada que presenta caries = cariada (la caries manda sobre la obturación).
//  - Los dientes temporales perdidos por exfoliación natural no cuentan.
//  - Sellantes y prótesis/coronas "indicadas" son plan de tratamiento, no índice.
// La ficha impresa (bloque J) solo trae columnas C, P, O (la "D" es el rótulo
// de la fila de dientes definitivos) y c, e, o (la "d" es el rótulo de temporales):
// una extracción indicada en permanente se cuenta como cariada (no hay columna propia).
// Se calcula sobre las piezas índice OMS (16/11/26/36/31/46 y 55/51/65/75/71/85).
import { PIEZAS_INDICE_OMS_CEO, PIEZAS_INDICE_OMS_CPO_CEO, type CodigoHallazgoOdontograma } from '@logiacare/hce-pdf';
import type { EntradaOdontogramaUi, EstadoOdontograma } from './tipos';

export interface IndiceCpod {
  readonly c: number;
  readonly p: number;
  readonly o: number;
  readonly total: number;
}

export interface IndiceCeod {
  readonly c: number;
  readonly e: number;
  readonly o: number;
  readonly total: number;
}

const CATEGORIA_PERMANENTE: Partial<Record<CodigoHallazgoOdontograma, 'c' | 'p' | 'o'>> = {
  caries: 'c',
  'extraccion-indicada': 'c',
  'endodoncia-por-realizar': 'c',
  obturado: 'o',
  'endodoncia-realizada': 'o',
  'corona-realizada': 'o',
  'protesis-fija-realizada': 'o',
  ausente: 'p',
  'perdida-otra-causa': 'p',
  'perdida-caries': 'p',
  'protesis-removible-realizada': 'p',
  'protesis-total-realizada': 'p',
};

const HALLAZGOS_DE_PERDIDA = new Set<CodigoHallazgoOdontograma>(['ausente', 'perdida-otra-causa', 'perdida-caries']);

const CATEGORIA_TEMPORAL: Partial<Record<CodigoHallazgoOdontograma, 'c' | 'e' | 'o'>> = {
  caries: 'c',
  'endodoncia-por-realizar': 'c',
  'extraccion-indicada': 'e',
  'perdida-caries': 'e',
  obturado: 'o',
  'endodoncia-realizada': 'o',
  'corona-realizada': 'o',
};

/**
 * Categoría de una pieza: caries en cualquier superficie manda (pieza restaurada con caries = cariada);
 * si no, el hallazgo de pieza completa; si no, obturación en alguna superficie.
 */
function categoriaDePieza<T extends string>(entrada: EntradaOdontogramaUi | undefined, tabla: Partial<Record<CodigoHallazgoOdontograma, T>>, cariada: T, obturada: T): T | undefined {
  const hallazgo = entrada?.hallazgo;
  // Pieza ausente/perdida: no hay superficies que contar, aunque el dato traiga alguna.
  if (hallazgo && HALLAZGOS_DE_PERDIDA.has(hallazgo)) return tabla[hallazgo];
  const estados = Object.values(entrada?.superficies ?? {});
  if (estados.includes('caries')) return cariada;
  const porHallazgo = hallazgo ? tabla[hallazgo] : undefined;
  if (porHallazgo) return porHallazgo;
  return estados.includes('obturado') ? obturada : undefined;
}

/** CPOD (C-P-O) sobre las 6 piezas índice OMS permanentes (16/11/26/36/31/46). */
export function calcularCpod(estado: EstadoOdontograma): IndiceCpod {
  let c = 0;
  let p = 0;
  let o = 0;
  for (const pieza of PIEZAS_INDICE_OMS_CPO_CEO) {
    const categoria = categoriaDePieza(estado[pieza], CATEGORIA_PERMANENTE, 'c', 'o');
    if (categoria === 'c') c++;
    else if (categoria === 'p') p++;
    else if (categoria === 'o') o++;
  }
  return { c, p, o, total: c + p + o };
}

/** ceo-d (c-e-o) sobre las 6 piezas índice OMS temporales (55/51/65/75/71/85). */
export function calcularCeod(estado: EstadoOdontograma): IndiceCeod {
  let c = 0;
  let e = 0;
  let o = 0;
  for (const pieza of PIEZAS_INDICE_OMS_CEO) {
    const categoria = categoriaDePieza(estado[pieza], CATEGORIA_TEMPORAL, 'c', 'o');
    if (categoria === 'c') c++;
    else if (categoria === 'e') e++;
    else if (categoria === 'o') o++;
  }
  return { c, e, o, total: c + e + o };
}
